type AnalyticsValue = string | number | boolean | null;

type AnalyticsParams = Record<string, AnalyticsValue | undefined>;

const sessionTrackedEventKeys = new Set<string>();

declare global {
  interface Window {
    dataLayer?: Array<Record<string, AnalyticsValue>>;
  }
}

const BLOCKED_PARAM_NAMES = new Set([
  'email',
  'name',
  'firstName',
  'lastName',
  'phone',
  'salonName',
  'companyName',
  'userId',
  'supabaseId',
  'salonId',
  'address',
  'ip',
  'ipAddress',
  'clientData',
]);

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function sanitizeParams(params?: AnalyticsParams): Record<string, AnalyticsValue> {
  if (!params) return {};

  const sanitized: Record<string, AnalyticsValue> = {};

  for (const [key, value] of Object.entries(params)) {
    if (BLOCKED_PARAM_NAMES.has(key)) {
      continue;
    }

    if (value === undefined) {
      continue;
    }

    sanitized[key] = value;
  }

  return sanitized;
}

export function trackEvent(eventName: string, params?: AnalyticsParams): void {
  try {
    if (!isBrowser()) return;

    const normalizedEventName = eventName.trim();
    if (!normalizedEventName) return;

    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
      event: normalizedEventName,
      ...sanitizeParams(params),
    });
  } catch {
    return;
  }
}

export function trackEventOncePerSession(
  eventName: string,
  sessionStorageKey: string,
  params?: AnalyticsParams,
): void {
  if (!isBrowser()) return;

  try {
    if (sessionTrackedEventKeys.has(sessionStorageKey)) return;

    try {
      if (window.sessionStorage.getItem(sessionStorageKey) === '1') {
        sessionTrackedEventKeys.add(sessionStorageKey);
        return;
      }
    } catch {
      // Continue with the in-memory guard when session storage is unavailable.
    }

    trackEvent(eventName, params);
    sessionTrackedEventKeys.add(sessionStorageKey);

    try {
      window.sessionStorage.setItem(sessionStorageKey, '1');
    } catch {
      return;
    }
  } catch {
    return;
  }
}

export function registrationCompletedSessionKey(userId: string, salonId: string): string {
  return `honey_signup_completed_tracked:${userId}:${salonId}`;
}
